import {useCallback, useEffect} from 'react';
import {useProductsStore} from '../store/productsStore';
import {Product} from '../models/product.model';

/**
 * Unified hook for products management that combines data and UI state
 * This replaces the need for multiple useState calls in components
 */
export const useProductsManagement = () => {
  // Single consolidated store
  const store = useProductsStore();

  // Destructure from consolidated store
  const {
    // Data
    products, brands, tags, productTypes, pagination, page,
    loading, brandsLoading, tagsLoading, typesLoading,
    error, brandsError, tagsError, typesError,
    fetchProducts, fetchBrands, fetchTags, fetchProductTypes,
    createProduct, updateProduct, setPage, setPageSize,
    refreshData, clearErrors,

    // UI State
    activeTab, setActiveTab,
    classificationsSubTab, setClassificationsSubTab,
    isDetailsModalOpen, openDetailsModal, closeDetailsModal,
    isAddStockFormOpen, openAddStockForm, closeAddStockForm,
    isAddProductModalOpen, openAddProductModal, closeAddProductModal,
    isEditProductModalOpen, openEditProductModal, closeEditProductModal,
    selectedProductForDetails, setSelectedProductForDetails,
    selectedProductForStock, setSelectedProductForStock,
    editProductId, setEditProductId,
    searchQuery, setSearchQuery, clearSearch,
    reset: resetUI
  } = store;

  // Filter products based on search
  const filteredProducts = products?.filter(product =>
    (product.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    (product.brandName?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    (product.barcode?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  ) || [];

  // Unified Actions
  const handleProductSelectForStock = useCallback((product: Product) => {
    setSelectedProductForStock(product);
    openAddStockForm();
  }, [setSelectedProductForStock, openAddStockForm]);

  const handleAddStockSuccess = useCallback(() => {
    closeAddStockForm();
    setSelectedProductForStock(null);
  }, [closeAddStockForm, setSelectedProductForStock]);

  const handleAddStockCancel = useCallback(() => {
    closeAddStockForm();
    setSelectedProductForStock(null);
  }, [closeAddStockForm, setSelectedProductForStock]);

  const handleViewProductDetails = useCallback((product: Product) => {
    // Ensure tags list is available to resolve tag names in the modal
    if (tags.length === 0) {
      fetchTags();
    }
    setSelectedProductForDetails(product);
    openDetailsModal();
  }, [tags.length, fetchTags, setSelectedProductForDetails, openDetailsModal]);

  const handleCloseDetailsModal = useCallback(() => {
    closeDetailsModal();
    setSelectedProductForDetails(null);
  }, [closeDetailsModal, setSelectedProductForDetails]);

  const handleAddProduct = useCallback(() => {
    openAddProductModal();
  }, [openAddProductModal]);

  const handleAddProductSuccess = useCallback(() => {
    closeAddProductModal();
    refreshData();
  }, [closeAddProductModal, refreshData]);

  const handleAddProductCancel = useCallback(() => {
    closeAddProductModal();
  }, [closeAddProductModal]);

  const handleEditProduct = useCallback((productId: number) => {
    setEditProductId(productId);
    openEditProductModal();
  }, [setEditProductId, openEditProductModal]);

  const handleEditProductSuccess = useCallback(() => {
    closeEditProductModal();
    setEditProductId(null);
    refreshData();
  }, [closeEditProductModal, setEditProductId, refreshData]);

  const handleEditProductCancel = useCallback(() => {
    closeEditProductModal();
    setEditProductId(null);
  }, [closeEditProductModal, setEditProductId]);

  const handleTabClick = useCallback((tab: typeof activeTab) => {
    setActiveTab(tab);
  }, [setActiveTab]);

  const handleClassificationsSubTabClick = useCallback((subTab: typeof classificationsSubTab) => {
    setClassificationsSubTab(subTab);
  }, [setClassificationsSubTab]);

  const handleProductDeleted = useCallback(() => {
    closeDetailsModal();
    setSelectedProductForDetails(null);
    refreshData();
  }, [closeDetailsModal, setSelectedProductForDetails, refreshData]);

  // Lazy load per tab
  useEffect(() => {
    if (activeTab === 'products' && products.length === 0) {
      fetchProducts();
    }
    if (activeTab === 'classifications') {
      if (brands.length === 0) { fetchBrands(); }
      if (tags.length === 0) { fetchTags(); }
      if (productTypes.length === 0) { fetchProductTypes(); }
    }
  }, [activeTab, products.length, brands.length, tags.length, productTypes.length, fetchProducts, fetchBrands, fetchTags, fetchProductTypes]);

  // Reset all stores
  const resetAllStores = useCallback(() => {
    clearErrors();
    resetUI();
  }, [clearErrors, resetUI]);

  return {
    // UI State
    activeTab, setActiveTab,
    classificationsSubTab, setClassificationsSubTab,
    isDetailsModalOpen, isAddStockFormOpen, isAddProductModalOpen, isEditProductModalOpen,
    selectedProductForDetails, selectedProductForStock, editProductId, setEditProductId,
    searchQuery, setSearchQuery, clearSearch,

    // Data
    products, filteredProducts, brands, tags, productTypes, pagination, page,

    // Loading States
    loading, brandsLoading, tagsLoading, typesLoading,

    // Error States
    error, brandsError, tagsError, typesError,

    // Actions
    fetchProducts, fetchBrands, fetchTags, fetchProductTypes,
    createProduct, updateProduct, setPage, setPageSize,
    handleProductSelectForStock, handleAddStockSuccess, handleAddStockCancel,
    handleViewProductDetails, handleCloseDetailsModal, handleProductDeleted,
    handleAddProduct, handleAddProductSuccess, handleAddProductCancel,
    handleEditProduct, handleEditProductSuccess, handleEditProductCancel,
    handleTabClick, handleClassificationsSubTabClick,
    refreshData, resetAllStores,

    // Direct modal actions
    openAddProductModal
  };
};
